import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { setActiveMission } from '../../utils/activeMission';
import { GAME_ROUTES, OPEN_PROGRESS } from '../../utils/fieldTasks';
import { can } from '../../utils/permissions';
import Sheet from '../ui/Sheet';
import Button, { buttonStyle } from '../ui/Button';

/*
 * Field Mode's Tasks sheet (docs/UI_HUBS_AND_GESTURES_PLAN.md section 5; plan
 * phase 6): what this person is to do out here, in three sections.
 *
 *   Assigned to you   missions a manager assigned them (migration 0100):
 *                     Accept, then Start in Field Mode (utils/activeMission.js),
 *                     so every specimen saved from then on is the mission's
 *   Your missions     their own open cards (GET /missions/mine)
 *   Your events       live field events they take part in, live first, each
 *                     opening /field/event/:id; and joining one with its code
 *                     (/join), now that the events list is the Admin hub's
 *
 * A ui/Sheet from the right, opened by the camera's Tasks chip and the
 * gallery's Tasks button, never by a swipe (no gesture edge). It lives in the
 * address (?sheet=tasks, useFieldSheet.js), and `focusMission` -- ?mission=
 * from the assignment email's link, /field?sheet=tasks&mission=<id> -- scrolls
 * to that card, puts the focus on its heading and marks it aria-current. A
 * mission that is gone, or not this person's, is said so.
 *
 * `tasks` is utils/fieldTasks.js's useFieldTasks(), shared with the chip that
 * counts them; the sheet asks again when it opens. `onStart` follows Start in
 * Field Mode: the camera closes the sheet, the gallery goes to the camera.
 */
export default function FieldTasksSheet({ open = true, ...props }) {
  if (!open) return null;
  return <OpenTasks {...props} />;
}

function OpenTasks({ onClose, user, tasks, focusMission = '', onStart, testId = 'field-tasks' }) {
  const { t, i18n } = useTranslation(['field', 'missions', 'events']);
  const lang = (i18n.language || 'en').startsWith('he') ? 'he' : 'en';
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState(null);
  const targetRef = useRef(null);
  const focused = useRef('');
  const ids = { assigned: useId(), missions: useId(), events: useId() };

  // Fresh when it opens: the chip's count may be minutes old.
  useEffect(() => { tasks.reload?.(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const ready = tasks.state === 'ready';
  const assigned = tasks.assigned || [];
  const missions = tasks.missions || [];
  const events = tasks.events || [];
  const target = focusMission || '';
  const found = !!target && (assigned.some((a) => a.mission_id === target) || missions.some((m) => m.mission_id === target));

  // The card the link named: after the sheet has taken the focus itself (its
  // title), so the focus goes back to the chip when the sheet closes.
  useEffect(() => {
    if (!found || focused.current === target) return undefined;
    const timer = setTimeout(() => {
      const heading = targetRef.current;
      if (!heading) return;
      focused.current = target;
      heading.scrollIntoView?.({ block: 'center' });
      heading.focus?.({ preventScroll: true });
    }, 0);
    return () => clearTimeout(timer);
  }, [found, target]);

  const title = (m) => (lang === 'he' ? m.title_he || m.title_en : m.title_en || m.title_he);
  const date = (iso) => (iso ? new Date(iso).toLocaleDateString(lang === 'he' ? 'he-IL' : 'en-GB') : '');

  const refused = (error) => {
    const code = error?.response?.data?.code;
    const known = {
      capacity_full: 'missions:messages.capacityFull', rank_required: 'missions:messages.rankRequired',
      reviewer_required: 'missions:messages.reviewerRequired', not_open: 'missions:messages.notOpen',
      already_accepted: 'missions:messages.notOpen',
    };
    setNotice({ kind: 'error', text: t(known[code] || 'missions:messages.failed') });
  };

  const accept = async (mission) => {
    if (busy) return;
    setBusy(mission.mission_id);
    setNotice(null);
    try {
      await api.post(`/missions/${mission.mission_id}/accept`);
      setNotice({ kind: 'ok', text: t('missions:messages.accepted') });
      await tasks.reload?.();
    } catch (error) {
      refused(error);
    } finally {
      setBusy('');
    }
  };

  const start = (mission) => {
    setActiveMission({ missionId: mission.mission_id, title: title(mission), until: mission.ends_at,
      userId: user?.user_id });
    onStart?.(mission);
  };

  const card = (mission, { assignment = null } = {}) => {
    const isTarget = mission.mission_id === target;
    const status = mission.progress?.status || null;
    const open = ['accepted', 'in_progress'].includes(status);
    const held = OPEN_PROGRESS.includes(status);
    const full = mission.slots_left === 0;
    const eligible = mission.eligible !== false;
    const desk = mission.tier === 'desk';
    const area = lang === 'he' ? mission.area?.name_he : mission.area?.name_en;
    const shownStatus = assignment ? t(`tasks.status.${assignment.status}`)
      : (status ? t(`missions:progress.status.${status}`) : null);
    return (
      <li key={`${assignment ? 'a' : 'm'}-${mission.mission_id}`} style={{ ...cardStyle, ...(isTarget ? targetStyle : null) }}
        aria-current={isTarget ? 'true' : undefined} data-testid={`${testId}-card`} data-mission={mission.mission_id}
        data-status={assignment ? assignment.status : status || 'none'}>
        <h4 ref={isTarget ? targetRef : undefined} tabIndex={-1} style={cardTitle}>{title(mission)}</h4>
        <p style={meta}>
          <span>{t(`missions:tier.${mission.tier}`)} · {t(`missions:kind.${mission.kind}`)}</span>
          {area ? <span> · {area}</span> : null}
          {mission.ends_at ? <span> · {t('tasks.until', { date: date(mission.ends_at) })}</span> : null}
        </p>
        {shownStatus && <p style={pill} data-testid={`${testId}-status`}>{shownStatus}</p>}
        {assignment && (
          <p style={meta}>
            {t('tasks.assignedBy', { name: assignment.assigned_by_name || '' })}
            {assignment.note ? <span style={note} data-testid={`${testId}-note`}>{assignment.note}</span> : null}
          </p>
        )}
        <div style={actions}>
          {!held && (
            <Button variant="primary" onClick={() => accept(mission)} disabled={!eligible || full || !!busy}
              busy={busy === mission.mission_id} data-testid={`${testId}-accept`}>
              {t('missions:actions.accept')}
            </Button>
          )}
          {open && desk && GAME_ROUTES[mission.kind] && (
            <Link to={GAME_ROUTES[mission.kind]} style={buttonStyle('primary')} data-testid={`${testId}-open-game`}>
              {t('missions:actions.openGame', { game: t(`missions:desk.game.${mission.kind}`) })}
            </Link>
          )}
          {open && !desk && (
            <Button variant="primary" onClick={() => start(mission)} data-testid={`${testId}-start`}>
              {t('missions:actions.start')}
            </Button>
          )}
        </div>
        {!held && (full || !eligible) && (
          <p style={meta}>{full ? t('missions:card.slotsFull')
            : t('missions:card.needsRank', { rank: t(`missions:rank.${mission.min_rank}`) })}</p>
        )}
      </li>
    );
  };

  const section = (key, heading, items, empty, extra = null) => (
    <section aria-labelledby={ids[key]} style={{ marginBlockEnd: 16 }} data-testid={`${testId}-${key}`}>
      <h3 id={ids[key]} style={sectionTitle}>{heading}</h3>
      {ready && items.length === 0 && <p style={meta}>{empty}</p>}
      {items.length > 0 && <ul style={list}>{items}</ul>}
      {extra}
    </section>
  );

  return (
    <Sheet side="right" title={t('tasks.title')} onClose={onClose} testId={testId}>
      {tasks.state === 'loading' && <p style={meta} role="status">{t('tasks.loading')}</p>}
      {ready && tasks.failed && (
        <p role="alert" style={{ ...meta, color: 'var(--c-danger-fg)' }} data-testid={`${testId}-failed`}>
          {t('tasks.failed')}{' '}
          <Button variant="ghost" onClick={() => tasks.reload?.()}>{t('tasks.retry')}</Button>
        </p>
      )}
      {ready && target && !found && (
        <p role="status" style={noticeStyle('info')} data-testid={`${testId}-gone`}>{t('tasks.gone')}</p>
      )}
      {notice && (
        <p role={notice.kind === 'error' ? 'alert' : 'status'} style={noticeStyle(notice.kind)}
          data-testid={`${testId}-notice`}>{notice.text}</p>
      )}
      {section('assigned', t('tasks.assigned'),
        assigned.map((a) => card(a.mission, { assignment: a })), t('tasks.noneAssigned'))}
      {section('missions', t('tasks.yours'), missions.map((m) => card(m)), t('tasks.noneYours'),
        <Link to="/game/missions" style={moreLink} data-testid={`${testId}-all-missions`}>{t('tasks.allMissions')}</Link>)}
      {section('events', t('tasks.events'), events.map((e) => (
        <li key={e.mission_id} style={cardStyle} data-testid={`${testId}-event`} data-phase={e.phase}>
          <Link to={`/field/event/${e.mission_id}`} style={eventLink}>
            <span style={cardTitle}>{lang === 'he' ? e.title_he || e.title_en : e.title_en || e.title_he}</span>
            <span style={pill}>{t(`events:phase.${e.phase}`)}</span>
          </Link>
          {e.meeting?.label && <p style={meta}>{e.meeting.label}</p>}
        </li>
      )), t('tasks.noneEvents'), can(user, 'event:play') ? (
        <Link to="/join" style={moreLink} data-testid={`${testId}-join-event`}>{t('tasks.joinEvent')}</Link>
      ) : null)}
    </Sheet>
  );
}

const list = { listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 10 };
const cardStyle = { padding: 12, borderRadius: 12, border: '1px solid var(--c-border)', background: 'var(--c-surface)' };
const targetStyle = { borderColor: 'var(--c-primary)', boxShadow: '0 0 0 2px var(--c-primary)' };
const cardTitle = { margin: 0, fontSize: 16, fontWeight: 600, outline: 'none' };
const sectionTitle = { margin: '0 0 8px', fontSize: 15 };
const meta = { margin: '4px 0', fontSize: 13, color: 'var(--c-text-muted-fg)' };
const note = { display: 'block', marginTop: 2, color: 'var(--c-text-body-fg)', whiteSpace: 'pre-wrap' };
const pill = { display: 'inline-block', margin: '6px 0 0', padding: '2px 10px', borderRadius: 999, fontSize: 12,
  background: 'var(--c-primary-soft-3)', border: '1px solid var(--c-border)', color: 'var(--c-text-body-fg)' };
const actions = { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 };
const moreLink = { display: 'inline-block', marginTop: 8, fontSize: 14, color: 'var(--c-primary-fg)' };
const eventLink = { display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap',
  color: 'inherit', textDecoration: 'none', minHeight: 44 };
const noticeStyle = (kind) => ({ margin: '0 0 12px', padding: '8px 12px', borderRadius: 8, fontSize: 14,
  background: { ok: 'var(--c-success-soft)', error: 'var(--c-danger-soft)' }[kind] || 'var(--c-info-soft)',
  color: 'var(--c-text-body-fg)' });
